import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Box,
  Stack,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  TablePagination,
  FormControl,
  FormControlLabel,
  FormHelperText,
  RadioGroup,
  Radio
} from '@mui/material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { Phone as PhoneIcon, School as SchoolIcon, DirectionsCar as TransportIcon } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../context/AuthContext';

const MyTasks = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [followUpDialog, setFollowUpDialog] = useState(false);
  const [followUpData, setFollowUpData] = useState({
    notes: '',
    status: 'contacted',
    follow_up_type: 'phone_call',
    contacted_by: '',
    recommendation: '',
    follow_up_date: new Date()
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [completionDialog, setCompletionDialog] = useState(false);
  const [completionReason, setCompletionReason] = useState('');
  const [completionConfirmed, setCompletionConfirmed] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  // Debug logging
  useEffect(() => {
    console.log('MyTasks component mounted/updated');
    console.log('Current user:', user);
    console.log('Current URL:', window.location.pathname);
  }, [user]);

  // Don't render until user is loaded
  if (!user) {
    console.log('MyTasks: User not loaded yet, showing loading...');
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      console.log('MyTasks: Fetching assignments for user:', user?.id);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/assignments/my-tasks?page=${page + 1}&limit=${rowsPerPage}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login', { state: { message: 'Your session has expired. Please log in again.' } });
        return;
      }

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch assignments');
      }

      setAssignments(result.data);
      setTotalCount(result.total);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, [page, rowsPerPage]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'contacted':
        return 'info';
      case 'completed':
        return 'success';
      case 'unreachable':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatAssignmentDate = (date) => {
    if (!date) return 'No date';
    return format(new Date(`${date.split('T')[0]}T12:00:00Z`), 'MMM d, yyyy');
  };

  const handleFollowUpClick = (assignment) => {
    console.log('Selected assignment for follow-up:', {
      id: assignment.id,
      first_timer_id: assignment.first_timer_id,
      fullAssignment: assignment
    });
    setSelectedAssignment(assignment);
    setFollowUpData(prev => ({
      ...prev,
      contacted_by: `${user?.first_name} ${user?.last_name}`
    }));
    setFollowUpDialog(true);
  };

  const handleFollowUpSubmit = async () => {
    if (!selectedAssignment?.id) {
      setError('Please try again.');
      return;
    }

    if (!followUpData.contacted_by || !followUpData.status) {
      setError('Please fill in all required fields.');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/follow-up/${selectedAssignment.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            notes: followUpData.notes,
            status: followUpData.status,
            follow_up_type: followUpData.follow_up_type,
            contacted_by: followUpData.contacted_by,
            recommendation: followUpData.recommendation,
            follow_up_date: followUpData.follow_up_date.toISOString()
          })
        }
      );

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login', { state: { message: 'Your session has expired. Please log in again.' } });
        return;
      }

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || 'Failed to submit follow-up');
        return;
      }

      fetchAssignments();
      setFollowUpDialog(false);
      setFollowUpData({
        notes: '',
        status: 'contacted',
        follow_up_type: 'phone_call',
        contacted_by: '',
        recommendation: '',
        follow_up_date: new Date()
      });
      setError('');
    } catch (error) {
      console.error('Error submitting follow-up:', error);
      setError('Network error. Please check your connection and try again.');
    }
  };

  const handlePhoneCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    if (newStatus === 'completed' && !completionConfirmed) {
      setCompletionDialog(true);
    } else {
      setFollowUpData(prev => ({
        ...prev,
        status: newStatus
      }));
    }
  };

  const handleCompletionConfirm = () => {
    if (!completionReason) {
      return;
    }
    setFollowUpData(prev => ({
      ...prev,
      status: 'completed',
      recommendation: completionReason,
      notes: prev.notes 
        ? `${prev.notes}\n\nCompletion Reason: ${completionReason}` 
        : `Completion Reason: ${completionReason}`
    }));
    setCompletionConfirmed(true);
    setCompletionDialog(false);
  };

  return (
    <Container maxWidth="xl">
      <Paper sx={{ p: 2, my: 3 }}>
        <Typography variant="h4" gutterBottom>
          My Tasks
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : !Array.isArray(assignments) || assignments.length === 0 ? (
          <Alert severity="info">
            No follow-up tasks assigned to you yet.
          </Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Service Date</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Additional Info</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Prayer Request</TableCell>
                    <TableCell>Last Follow-up</TableCell>
                    <TableCell>Followed By</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map((assignment) => (
                    <TableRow key={assignment.id}>
                      <TableCell>
                        <Chip 
                          label={assignment.follow_up_status || assignment.assignment_status || 'pending'} 
                          color={getStatusColor(assignment.follow_up_status || assignment.assignment_status || 'pending')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {formatAssignmentDate(assignment.service_date)}
                      </TableCell>
                      <TableCell>
                        {assignment.first_name} {assignment.last_name}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1}>
                          <Typography variant="body2">
                            {assignment.gender || 'Not specified'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {assignment.city || 'No city specified'}
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            {assignment.is_student && (
                              <Tooltip title="Student">
                                <SchoolIcon fontSize="small" color="info" />
                              </Tooltip>
                            )}
                            {assignment.needs_transportation && (
                              <Tooltip title="Needs Transportation">
                                <TransportIcon fontSize="small" color="warning" />
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {assignment.phone_number}
                          </Typography>
                          {assignment.phone_number && (
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handlePhoneCall(assignment.phone_number)}
                            >
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: '200px',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word'
                          }}
                        >
                          {assignment.prayer_request || 'No prayer request provided'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {assignment.last_follow_up_date ? 
                          formatAssignmentDate(assignment.last_follow_up_date) : 
                          'No follow-up yet'
                        }
                      </TableCell>
                      <TableCell>{assignment.last_followed_by || '-'}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleFollowUpClick(assignment)}
                        >
                          Add Follow-up
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Box>
          </>
        )}

        <Dialog open={followUpDialog} onClose={() => setFollowUpDialog(false)}>
          <DialogTitle>Add Follow-up</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {selectedAssignment && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1">
                  First Timer: {selectedAssignment.first_name} {selectedAssignment.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Service Date: {formatAssignmentDate(selectedAssignment.service_date)}
                </Typography>
              </Box>
            )}
            
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Follow-up Date"
                value={followUpData.follow_up_date}
                onChange={(newValue) => setFollowUpData({ ...followUpData, follow_up_date: newValue })}
                maxDate={new Date()}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    required
                    error={followUpData.follow_up_date > new Date()}
                    helperText={followUpData.follow_up_date > new Date() ? "Date cannot be in the future" : ""}
                  />
                )}
              />
            </LocalizationProvider>
            
            <TextField
              select
              fullWidth
              margin="normal"
              label="Follow-up Type"
              value={followUpData.follow_up_type}
              onChange={(e) => setFollowUpData({ ...followUpData, follow_up_type: e.target.value })}
            >
              <MenuItem value="phone_call">Phone Call</MenuItem>
              <MenuItem value="home_visit">Home Visit</MenuItem>
              <MenuItem value="homecell">Homecell</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            
            <TextField
              fullWidth
              margin="normal"
              label="Contacted By"
              value={followUpData.contacted_by}
              onChange={(e) => setFollowUpData({ ...followUpData, contacted_by: e.target.value })}
              required
              disabled
            />
            
            <TextField
              select
              fullWidth
              margin="normal"
              label="Status"
              value={followUpData.status}
              onChange={handleStatusChange}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="contacted">Contacted</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="unreachable">Unreachable</MenuItem>
            </TextField>
            
            <TextField
              select
              fullWidth
              margin="normal"
              label="Recommendation"
              value={followUpData.recommendation}
              onChange={(e) => setFollowUpData({ ...followUpData, recommendation: e.target.value })}
            >
              <MenuItem value="more_follow_up">Needs More Follow-up</MenuItem>
              <MenuItem value="stronger_follow_up">Needs Stronger Follow-up</MenuItem>
              <MenuItem value="established">Established</MenuItem>
              <MenuItem value="non_permanent_visitor">Non-permanent Visitor</MenuItem>
              <MenuItem value="not_reachable">Not Reachable</MenuItem>
              <MenuItem value="add_to_homecell">Add to Homecell</MenuItem>
              <MenuItem value="join_service_group">Join Service Group</MenuItem>
            </TextField>
            
            <TextField
              fullWidth
              margin="normal"
              label="Follow-up Notes"
              multiline
              rows={4}
              value={followUpData.notes}
              onChange={(e) => setFollowUpData({ ...followUpData, notes: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFollowUpDialog(false)}>Cancel</Button>
            <Button onClick={handleFollowUpSubmit} variant="contained">
              Submit Follow-up
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog 
          open={completionDialog} 
          onClose={() => {
            setCompletionDialog(false);
            setFollowUpData(prev => ({
              ...prev,
              status: 'contacted'
            }));
            // Ensure focus is moved back to the trigger button
            const triggerButton = document.querySelector('[aria-label="Complete Follow-up"]');
            if (triggerButton) {
              triggerButton.focus();
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Follow-up Completion</DialogTitle>
          <DialogContent>
            <Typography color="warning.main" sx={{ mb: 2 }}>
              Please note: Once marked as completed, this follow-up cannot be edited further.
            </Typography>
            <Typography sx={{ mb: 2 }}>
              Only mark as completed if no further follow-up is needed. This should only be used in these specific cases:
            </Typography>
            <Box sx={{ mb: 3, bgcolor: 'info.main', color: 'white', p: 2, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • <strong>Established</strong> - They are now regularly attending and actively involved
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • <strong>Non-permanent Visitor</strong> - They have confirmed they were just visiting
              </Typography>
              <Typography variant="body2">
                • <strong>Unreachable</strong> - Multiple attempts made with no success
              </Typography>
            </Box>
            <Typography color="info.main" sx={{ mb: 2 }}>
              For all other cases (e.g., needs more follow-up, interested but not yet established), please use the "Contacted" status instead.
            </Typography>
            <FormControl required fullWidth>
              <RadioGroup
                value={completionReason}
                onChange={(e) => setCompletionReason(e.target.value)}
              >
                <FormControlLabel 
                  value="established" 
                  control={<Radio />} 
                  label="Established - Actively attending church and integrated into a department/homecell" 
                />
                <FormControlLabel 
                  value="non_permanent_visitor" 
                  control={<Radio />} 
                  label="Non-permanent Visitor - Confirmed they were just visiting" 
                />
                <FormControlLabel 
                  value="not_reachable" 
                  control={<Radio />} 
                  label="Unreachable - Multiple attempts made but cannot be reached" 
                />
              </RadioGroup>
            </FormControl>
            {!completionReason && (
              <FormHelperText error>
                Please select a reason for completion
              </FormHelperText>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setCompletionDialog(false);
              setFollowUpData(prev => ({
                ...prev,
                status: 'contacted'
              }));
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCompletionConfirm}
              variant="contained"
              disabled={!completionReason}
            >
              Confirm Completion
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default MyTasks; 